import React, { useState } from "react"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"
import Modal from "~tailwindui/Modal"

import classNames from "src/classNames"

import { gql, useClient, useMutation } from "hooks/urql"

import ModalWithProvidedBody from "components/ModalWithProvidedBody"

export type EmergencyEndSessionProps = {
  sessionId: string
  userType: "Student" | "Tutor" | "Teacher" | "Admin"
}

const EmergencyEndSession: React.FC<EmergencyEndSessionProps> = ({
  sessionId,
  userType,
}) => {
  const client = useClient()
  const [mutationState, runMutation]: any[] = useMutation(
    endSessionMutation,
    client
  )

  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>()

  const closeCloudroom = () => {
    runMutation({ sessionId }).then(response => {
      const { failures } = response.data.closeCloudroom
      if (failures.length > 0) {
        setError(failures[0].message)
      }
    })
  }

  if (!["Admin", "Teacher"].includes(userType)) return null

  return userType === "Admin" ? (
    <ModalWithProvidedBody
      modalTitle="EMERGENCY END SESSION"
      buttonText="EMERGENCY! END SESSION FOR ALL USERS"
      buttonClassName="btn btn-danger"
    >
      {({ closeModal }) => (
        <>
          <div className="modal-body">
            <AlertMessageBox level={AlertLevel.Warning}>
              THIS WILL END THE CLOUDROOM SESSION FOR ALL USERS.
            </AlertMessageBox>

            <p className="lead">
              If you just want to leave the room, simply close your browser tab.
            </p>

            {error && <AlertMessageBox>{error}</AlertMessageBox>}
          </div>

          <div className="modal-footer">
            <div className="grid w-full grid-cols-2 gap-x-4">
              <button
                className="btn btn-outline-info whitespace-normal"
                type="button"
                onClick={closeModal}
              >
                No, do not end this session.
              </button>
              <button
                className={classNames(
                  "btn btn-danger whitespace-normal",
                  mutationState.fetching && "disabled"
                )}
                type="button"
                onClick={closeCloudroom}
                disabled={mutationState.fetching}
              >
                YES. Close this cloudroom immediately.
              </button>
            </div>
          </div>
        </>
      )}
    </ModalWithProvidedBody>
  ) : (
    <>
      <button
        className="rounded bg-brand-700 px-3 py-1 font-semibold"
        onClick={() => setOpen(true)}
      >
        EMERGENCY! END SESSION FOR ALL USERS
      </button>
      <Modal.Dialog hideButton isOpen={open} closeModal={() => setOpen(false)}>
        <Modal.Header>EMERGENCY END SESSION</Modal.Header>
        <Modal.Body>
          <AlertMessageBox level={AlertLevel.Warning}>
            THIS WILL END THE CLOUDROOM SESSION FOR ALL USERS.
          </AlertMessageBox>

          <p className="mb-3">
            If you just want to leave the room, simply close your browser tab.
          </p>

          {error && <AlertMessageBox>{error}</AlertMessageBox>}

          <div className="flex">
            <button
              className="mr-4 rounded bg-info-700 px-3 py-1 font-semibold text-white"
              onClick={() => setOpen(false)}
            >
              No, do not end this session.
            </button>
            <button
              className={classNames(
                "rounded bg-brand-700 px-3 py-1 font-semibold text-white",
                mutationState.fetching && "opacity-50"
              )}
              type="button"
              onClick={closeCloudroom}
              disabled={mutationState.fetching}
            >
              YES. Close this cloudroom immediately.
            </button>
          </div>
        </Modal.Body>
      </Modal.Dialog>
    </>
  )
}

const endSessionMutation = gql`
  mutation ($sessionId: ID!) {
    closeCloudroom(sessionId: $sessionId) {
      failures {
        message
      }
    }
  }
`

export default EmergencyEndSession
